// File: theme.js
//
// Two layers of customization, both backed by CSS custom properties:
//
//   1. COLORS — the brand palette (greenBase, aquaBase, etc.). Each
//      entry resolves to "var(--color-<name>)".
//   2. THEME  — semantic "roles" (primary, backgroundBottom, headerBg,
//      etc.) that point AT a palette entry. Each color-role resolves
//      to "var(--theme-<role>)", which in turn aliases a --color-X.
//
// Why two layers?  So an admin can do BOTH "change aquaBase to a new
// hex" AND "swap which color the backgroundBottom role uses" without
// touching code. The role mapping lives in Firestore and is loaded /
// re-applied by App.js the same way colors are.

// ===== Default brand palette =====
//
// Grouped by family. The color editor renders rows in this order, so
// keeping families together makes related shades sit next to each
// other on screen.
export const DEFAULT_COLORS = {
  aquaBase: "#00A7BF",
  aquaLight: "#99F2FF",
  aquaDark: "#00606E",
  aquaDeep: "#003840",

  greenBase: "#82BF35",
  greenLight: "#E7F7D2",
  greenDark: "#407300",
  greenDeep: "#1F3800",

  purpleBase: "#7f3f98",
  purpleLight: "#cbb1d7",
  purpleDark: "#5d2c70",
  purpleDeep: "#2d1238",

  yellowBase: "#ECD353",
  yellowLight: "#FFEA87",
  yellowDark: "#998000",

  brown: "#3D3300",
};

export const COLOR_KEYS = Object.keys(DEFAULT_COLORS);

// COLORS[key] === "var(--color-<key>)". Existing inline styles like
// `style={{ backgroundColor: COLORS.greenBase }}` keep working — the
// browser resolves the CSS variable at render time, so when the
// variable's value changes, the rendered color updates automatically
// with no React re-render.
export const COLORS = Object.fromEntries(
  COLOR_KEYS.map((key) => [key, `var(--color-${key})`])
);

// ===== Default role assignments =====
//
// Each role maps to a color-palette key. The Theme editor lets admins
// re-assign a role to a different palette key (e.g. backgroundBottom
// switches from purpleBase to aquaBase).
export const DEFAULT_ROLES = {
  primary: "greenBase",
  secondary: "purpleBase",
  highlight: "yellowBase",
  text: "greenDeep",
  mutedText: "greenDark",
  headerBg: "greenDark",
  backgroundTop: "greenBase",
  backgroundMid: "greenLight",
  backgroundBottom: "purpleBase",
};

export const ROLE_KEYS = Object.keys(DEFAULT_ROLES);

// THEME entries that are "roles" (point at a palette color) become
// var(--theme-<role>). Static rgba/static values stay as fixed
// strings — they aren't user-editable.
export const THEME = {
  primary: "var(--theme-primary)",
  secondary: "var(--theme-secondary)",
  highlight: "var(--theme-highlight)",

  text: "var(--theme-text)",
  mutedText: "var(--theme-mutedText)",

  headerBg: "var(--theme-headerBg)",
  headerText: "#ffffff",

  backgroundTop: "var(--theme-backgroundTop)",
  backgroundMid: "var(--theme-backgroundMid)",
  backgroundBottom: "var(--theme-backgroundBottom)",

  cardBg: "rgba(255,255,255,0.94)",
  cardBgSoft: "rgba(255,255,255,0.82)",
  surface: "rgba(255,255,255,0.82)",
  surfaceStrong: "rgba(255,255,255,0.94)",
  surfaceTint: "rgba(231,247,210,0.58)",
  border: "rgba(31,56,0,0.14)",
  shadow: "0 18px 38px rgba(31,56,0,0.14)",
};

// The page background gradient. Built from the three "background*"
// THEME roles, so re-assigning any of them in the editor updates the
// gradient on every page instantly.
export const pageBackground = {
  background: `linear-gradient(180deg, ${THEME.backgroundTop} 0%, ${THEME.backgroundMid} 50%, ${THEME.backgroundBottom} 100%)`,
  backgroundAttachment: "fixed",
};

// ===== DOM application helpers =====

// Apply hex overrides for any subset of the brand palette. Keys not in
// the object are left untouched; invalid hex values are skipped.
export function applyColors(colors) {
  if (!colors || typeof colors !== "object") return;
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    if (
      typeof key === "string" &&
      /^[a-zA-Z][a-zA-Z0-9_]*$/.test(key) &&
      typeof value === "string" &&
      /^#[0-9a-fA-F]{3,8}$/.test(value)
    ) {
      root.style.setProperty(`--color-${key}`, value);
    }
  });
}

// Apply role-to-color assignments. Each entry is { roleName: colorKey },
// and we point --theme-<roleName> at var(--color-<colorKey>). Browser
// resolves the chain at render time, so changing a role re-paints any
// element using THEME.<role> automatically.
export function applyRoles(roles) {
  if (!roles || typeof roles !== "object") return;
  const root = document.documentElement;
  Object.entries(roles).forEach(([roleName, colorKey]) => {
    if (
      typeof roleName === "string" &&
      /^[a-zA-Z][a-zA-Z0-9_]*$/.test(roleName) &&
      typeof colorKey === "string" &&
      /^[a-zA-Z][a-zA-Z0-9_]*$/.test(colorKey)
    ) {
      root.style.setProperty(
        `--theme-${roleName}`,
        `var(--color-${colorKey})`
      );
    }
  });
}

// Seed both the default palette and the default role assignments.
// Called both at module load (side effect below) and from App.js on
// mount, so the CSS variables are populated before the first paint.
export function initThemeDefaults() {
  applyColors(DEFAULT_COLORS);
  applyRoles(DEFAULT_ROLES);
}

// Side effect on module load: synchronously seed the CSS variables
// with the defaults. This runs before any React component first
// renders, so there's no flash of unstyled (variable-less) colors
// while we wait for the App.js useEffect to fire.
if (typeof document !== "undefined") {
  initThemeDefaults();
}
